import { jsxs, jsx } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import "react";
import "../shared/quiz-style-context.js";
import { QuizResultToggle } from "../shared/quiz-result-toggle.js";
import { SubmitButton } from "../shared/submit-button.js";
import { QuizImage } from "../shared/quiz-image.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import "../../../context/DevToolsContext.js";
import { QuizHeader } from "../shared/quiz-header.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { useInlineQuiz } from "../hooks/use-inline-quiz.js";
function InlineStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false
}) {
  const { t } = useTranslation("quiz");
  const {
    children,
    selectedKeysByChild,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolvedCorrectKeySetByChild,
    perChildCorrectsBySelectedKey,
    handleSelectChild,
    handleSubmit
  } = useInlineQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers,
    onAnswerChange
  });
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      QuizHeader,
      {
        quizId,
        typeLabel: t("types.inline.label"),
        title: quiz.title,
        difficultyLevel,
        variant: "student",
        image: quiz.image,
        imageMobile: quiz.imageMobile,
        desmos: quiz.desmos,
        vars: quiz.vars,
        dimTitleText: dimChoiceText,
        dimProblemText: dimChoiceText,
        dimMedia: dimChoiceText,
        problem: quiz.problem && /* @__PURE__ */ jsx("div", { className: "text-xl leading-relaxed text-gray-900", children: formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? void 0) })
      }
    ),
    children.map((child, childIdx) => /* @__PURE__ */ jsx(
      InlineChildBlockStudent,
      {
        child,
        childIdx,
        selectedKeys: selectedKeysByChild[childIdx] ?? [],
        isSolved,
        isViewSolved,
        shouldShowCorrectAnswers,
        resolvedCorrectKeySet: resolvedCorrectKeySetByChild[childIdx] ?? /* @__PURE__ */ new Set(),
        correctsBySelectedKey: perChildCorrectsBySelectedKey[childIdx] ?? /* @__PURE__ */ new Map(),
        onSelect: (key) => handleSelectChild(childIdx, key),
        dimChoiceText,
        t
      },
      child.scriptId || childIdx
    )),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(SubmitButton, { onClick: handleSubmit, disabled: !canSubmit, label: submitLabel }),
    isSolved && !hideExplanation && /* @__PURE__ */ jsx(
      QuizResultToggle,
      {
        value: isShowingCorrectAnswers ? "correct-and-explanation" : "my-answer",
        onChange: (next) => setIsShowingCorrectAnswers(next === "correct-and-explanation")
      }
    ),
    isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: (solveResult == null ? void 0 : solveResult.description) ?? void 0,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: false
      }
    )
  ] });
}
function InlineChildBlockStudent({
  child,
  childIdx,
  selectedKeys,
  isSolved,
  isViewSolved,
  shouldShowCorrectAnswers,
  resolvedCorrectKeySet,
  correctsBySelectedKey,
  onSelect,
  dimChoiceText = false,
  t
}) {
  const choices = child.random && Array.isArray(child.random) && child.random.length > 0 ? child.random.map((i) => child.choices[i]).filter(Boolean) : child.choices;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsx("div", { className: "text-sm font-bold text-lc-gray-500", children: t("types.inline.childLabel", { n: childIdx + 1 }) }),
    child.problem && /* @__PURE__ */ jsx("div", { className: cn("text-lg leading-relaxed text-gray-900", dimChoiceText && "opacity-[0.05]"), children: /* @__PURE__ */ jsx(KatexText, { text: child.problem }) }),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "flex flex-wrap gap-3",
        role: "listbox",
        "aria-multiselectable": "true",
        children: choices.map((choice, i) => {
          const isSelected = selectedKeys.includes(choice.key);
          const isInResolvedCorrect = resolvedCorrectKeySet.has(choice.key);
          const isCorrectByCorrects = correctsBySelectedKey.get(choice.key);
          const showAsCorrect = isSolved && (shouldShowCorrectAnswers ? isInResolvedCorrect : isSelected && (isCorrectByCorrects !== void 0 ? isCorrectByCorrects : isInResolvedCorrect));
          const isWrongSelected = isSolved && !shouldShowCorrectAnswers && isSelected && !showAsCorrect;
          const isDisplayedSelected = shouldShowCorrectAnswers ? isInResolvedCorrect : isSelected;
          const isCompleted = isSolved || isViewSolved;
          return /* @__PURE__ */ jsxs(
            "button",
            {
              role: "option",
              "aria-selected": isDisplayedSelected,
              onClick: () => !isCompleted && onSelect(choice.key),
              className: cn(
                "inline-flex items-center gap-2 px-4 py-2 rounded-2xl border-2 text-base font-medium transition-all",
                !isSolved && !isSelected && "text-gray-900 border-lc-gray-400 [box-shadow:0_4px_0_rgba(0,0,0,0.1)]",
                !isSolved && !isSelected && "hover:text-lc-lightblue-900 hover:border-lc-lightblue-900",
                !isSolved && isSelected && "text-lc-lightblue-dark bg-lc-lightblue-200 border-lc-lightblue-900",
                showAsCorrect && "text-lc-mint-900 bg-lc-mint-100 border-lc-mint-900",
                isWrongSelected && "text-lc-red-900 bg-lc-red-100 border-lc-red-900",
                isSolved && !showAsCorrect && !isWrongSelected && "border-lc-gray-400 text-gray-900 opacity-60",
                isCompleted && "pointer-events-none"
              ),
              children: [
                /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: cn(
                      "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0",
                      !isSelected && !isSolved && "text-lc-gray-500 bg-lc-gray-100",
                      isSelected && !isSolved && "text-white bg-lc-lightblue-900",
                      showAsCorrect && "text-white bg-lc-mint-900",
                      isWrongSelected && "text-white bg-lc-red-900",
                      isSolved && !showAsCorrect && !isWrongSelected && "text-lc-gray-500 bg-lc-gray-100"
                    ),
                    children: i + 1
                  }
                ),
                choice.image && /* @__PURE__ */ jsx("div", { className: cn(dimChoiceText && "opacity-[0.05]"), children: /* @__PURE__ */ jsx(
                  QuizImage,
                  {
                    image: choice.image,
                    alt: t("types.multiChoice.optionAlt", { number: i + 1 }),
                    className: "my-0 shrink-0"
                  }
                ) }),
                choice.answer && /* @__PURE__ */ jsx("span", { "data-quiz-choice-text": true, className: cn(dimChoiceText && "opacity-[0.05]"), children: /* @__PURE__ */ jsx(KatexText, { text: choice.answer }) })
              ]
            },
            choice.key
          );
        })
      }
    )
  ] });
}
export {
  InlineStudent
};
//# sourceMappingURL=inline-student.js.map
